import { useState } from "react";
import { Mail, ShieldCheck, Sparkles, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";

const PERKS = ["Track every order and delivery", "Faster checkout with saved details", "One-time email code — no passwords"];

export const SignInPanel = ({ accountUrl }) => {
  const { login } = useAuth();
  const [busy, setBusy] = useState(false);

  const startLogin = async () => {
    setBusy(true);
    try {
      await login();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start sign-in. Please try again.");
      setBusy(false);
    }
  };

  return (
    <main data-testid="account-page" className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
      <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">Everyday Essentials Account</p>
          <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Sign in or create your account</h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-500 sm:text-base">
            One account for your orders, deliveries and details. New here? Signing in with your email creates your
            account automatically — nothing else to fill in.
          </p>
          <ul className="mt-6 space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-neutral-700">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-neutral-900 text-white">
                  <Sparkles size={11} />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-neutral-200 bg-white p-8 shadow-[0_24px_60px_-40px_rgba(0,0,0,0.35)]">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-neutral-100">
              <User size={22} className="text-neutral-500" />
            </div>
            <h2 className="mt-5 text-center font-heading text-lg font-bold">Continue with your email</h2>
            <p className="mt-1 text-center text-sm text-neutral-500">
              We'll email you a 6-digit code to sign in securely.
            </p>
            <button
              data-testid="account-login-button"
              onClick={startLogin}
              disabled={busy}
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-neutral-900 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-700 disabled:opacity-60"
            >
              <Mail size={16} />
              {busy ? "Redirecting to secure sign-in..." : "Sign in with Email"}
            </button>
            <p className="mt-5 flex items-center justify-center gap-1.5 text-xs text-neutral-400">
              <ShieldCheck size={13} /> Secured by Shopify — your code is verified on Shopify's servers
            </p>
            {accountUrl && (
              <p className="mt-4 text-center text-xs text-neutral-400">
                Trouble signing in?{" "}
                <a
                  href={accountUrl}
                  data-testid="account-hosted-link"
                  className="font-semibold text-neutral-700 underline underline-offset-4"
                >
                  Use our account portal
                </a>
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </main>
  );
};
